<template>
  <z-card-modal width="auto" :opened="value" @change="$emit('input', $event)">
    <template #content>
      <div class="custom-title mb-4">{{ $t('title') }}</div>

      <div class="content-container">
        <z-scrollbar style="max-height: 200px; flex: 1">
          <v-list
            v-if="filterGroups.length > 0"
            class="pa-0"
            nav
            dense
            max-width="250"
            min-width="170"
            style="background-color: unset"
          >
            <v-list-item-group v-model="selectedGroup" color="var(--accent)">
              <template v-for="(group, index) in internalFilterGroups">
                <v-hover v-slot="{hover}" :key="index" open-delay="200">
                  <v-list-item :value="group">
                    <template #default="{active}">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ group.name }}
                        </v-list-item-title>
                      </v-list-item-content>

                      <v-list-item-action>
                        <v-fade-transition mode="out-in">
                          <z-icon v-if="active" size="15">
                            $check_rounded
                          </z-icon>

                          <z-icon
                            v-else-if="hover"
                            size="15"
                            color="var(--black)"
                            @click.stop="onDeleteGroup(group)"
                          >
                            $delete
                          </z-icon>
                        </v-fade-transition>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                </v-hover>
              </template>
            </v-list-item-group>
          </v-list>

          <div v-else class="empty-list-text">
            {{ $t('empty_groups') }}
          </div>
        </z-scrollbar>

        <v-divider vertical></v-divider>

        <template v-if="selectedGroup != null">
          <div class="d-flex flex-column">
            <div class="custom-title mb-2">{{ $t('selected_filters') }}</div>

            <z-scrollbar style="max-height: 200px; min-width: 200px">
              <z-group-select
                v-for="(filter, index) of selectedGroup.filters"
                :key="index"
                class="mb-2"
                height="30"
                :label="filter.text"
                :description="
                  Array.isArray(filter.value)
                    ? filter.value.join(', ')
                    : filter.value
                "
              ></z-group-select>
            </z-scrollbar>
          </div>
        </template>

        <div v-else class="empty-list-text">
          {{ $t('no_group_selected') }}
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-end align-center">
        <z-btn
          small
          rounded
          :text="$t('cancel')"
          @click="$emit('input', false)"
        ></z-btn>

        <z-btn
          v-if="selectedGroup != null"
          class="ml-2"
          primary
          small
          rounded
          :text="$t('include_filters')"
          @click="onIncludeFilters"
        ></z-btn>
      </div>
    </template>
  </z-card-modal>
</template>

<script>
import {cloneDeep} from 'lodash'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },

    filterGroups: {
      type: Array,
      default: () => [],
    },
  },

  i18n: {
    messages: {
      en: {
        title: 'Manage saved filters',
        selected_filters: 'Selected filters',
        cancel: 'Cancel',
        include_filters: 'Include filters',
        no_group_selected: 'No filter group selected',
        empty_groups: 'There are no groups to select',
      },
      pt: {
        title: 'Gerenciar filtros salvos',
        selected_filters: 'Filtros selecionados',
        cancel: 'Cancelar',
        include_filters: 'Incluir filtros',
        no_group_selected: 'Nenhum grupo de filtros selecionado',
        empty_groups: 'Não existem grupos para seleção',
      },

      es: {
        title: 'Administrar filtros guardados',
        selected_filters: 'Filtros seleccionados',
        cancel: 'Cancelar',
        include_filters: 'Incluir filtros',
        no_group_selected: 'Ningún grupo de filtros seleccionado',
        empty_groups: 'No hay grupos para seleccionar',
      },
      fr: {
        title: 'Gérer les filtres enregistrés',
        selected_filters: 'Filtres sélectionnés',
        cancel: 'Annuler',
        include_filters: 'Inclure les filtres',
        no_group_selected: 'Aucun groupe de filtres sélectionné',
        empty_groups: 'Aucun groupe à sélectionner',
      },
      tr: {
        title: 'Kayıtlı filtreleri yönet',
        selected_filters: 'Seçilen filtreler',
        cancel: 'İptal',
        include_filters: 'Filtreleri dahil et',
        no_group_selected: 'Seçili filtre grubu yok',
        empty_groups: 'Seçilecek grup yok',
      },
    },
  },

  data() {
    return {
      selectedGroup: null,
    }
  },

  watch: {
    filterGroups: {
      immediate: true,
      deep: true,
      handler(value) {
        this.internalFilterGroups = cloneDeep(value)
      },
    },
  },

  methods: {
    onIncludeFilters() {
      this.$emit('include-filters', this.selectedGroup)
      this.$emit('input', false)
    },

    onDeleteGroup(group) {
      this.$emit('delete-group', group)
    },
  },
}
</script>

<style scoped>
:deep(.v-list-item) {
  height: 30px !important;
  min-height: 30px !important;
}

.empty-list-text {
  margin: 50px 0;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  line-height: 15px;
  color: var(--black-600);
}

.custom-title {
  /* Body/Default Title Case */
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 18px */
  text-transform: capitalize;
}

.content-container {
  display: flex;
  justify-content: space-between;
  gap: 16px;

  .selected-filter {
    display: flex;
    align-items: center;
    background-color: var(--accent);
    justify-content: space-between;
    color: var(--white);
    padding: 4px 8px;
    border-radius: 10px;
    margin-bottom: 8px;

    .infos {
      display: flex;
      flex-direction: column;
      gap: 4px;

      & > :nth-child(1) {
        font-size: 11px;
        font-weight: 400;
        text-transform: uppercase;
      }

      & > :nth-child(2) {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
      }
    }
  }
}
</style>
